import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SideBar from "../sidebar/SideBar";
import { formatDateRange } from '../utils/dateUtils';

import firebaseApp from '../Firebase';
import { collection, getFirestore, query, getDocs } from "firebase/firestore";

const ProjectSummaries = ({ path, title, summary, startDate, endDate }) => {
    const url = "/projects/" + path;
    const dateRange = formatDateRange(startDate, endDate);

    return (
        <a href={url} className='block group'>
            <div className='glass-card-hover p-6 h-full flex flex-col'>
                <div className='flex items-start justify-between mb-3'>
                    <h3 className='text-2xl font-bold text-white group-hover:gradient-text transition-all duration-300'>
                        {title}
                    </h3>
                    <svg className='w-5 h-5 text-slate-400 group-hover:text-emerald-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                    </svg>
                </div>
                <div className='flex items-center gap-2 mb-4'>
                    <svg className='w-4 h-4 text-slate-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                    </svg>
                    <span className='text-sm text-slate-400 font-medium'>{dateRange}</span>
                </div>
                <p className='text-slate-300 leading-relaxed flex-grow'>{summary}</p>
                <div className='mt-4 pt-4 border-t border-slate-700/50'>
                    <span className='text-sm font-medium text-emerald-400 group-hover:text-emerald-300 transition-colors'>
                        Read more →
                    </span>
                </div>
            </div>
        </a>
    );
};

ProjectSummaries.propTypes = {
    path: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired
};

const ProjectSummary = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const ref = getFirestore(firebaseApp);
        const q = query(collection(ref, "projects"));
        getDocs(q)
            .then((querySnapshot) => {
                const projects = [];
                querySnapshot.forEach((doc) => {
                    projects.push(doc.data());
                });
                setProjects(projects);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching projects:", err);
                setError("Failed to load projects. Please try again later.");
                setLoading(false);
            });
    }, []);

    if (loading) {
        return (
            <>
                <SideBar />
            </>
        );
    }

    if (error) {
        return (
            <>
                <SideBar />
                <div className="min-h-screen flex items-center justify-center px-6 pb-20 md:pl-24 md:pb-0 md:pr-12 lg:pl-32 lg:pr-20">
                    <div className="glass-card p-8 max-w-md text-center">
                        <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-xl font-bold text-white mb-2">Oops!</h2>
                        <p className="text-red-400">{error}</p>
                    </div>
                </div>
            </>
        );
    }

    if (projects.length === 0) {
        return (
            <>
                <SideBar />
                <div className="min-h-screen flex items-center justify-center px-6 pb-20 md:pl-24 md:pb-0 md:pr-12 lg:pl-32 lg:pr-20">
                    <div className="glass-card p-8 max-w-md text-center">
                        <p className="text-slate-400">No projects found.</p>
                    </div>
                </div>
            </>
        );
    }

    const featured = [];
    const normal = [];
    projects.forEach(element => {
        if (element.featured) {
            featured.push(element);
        } else {
            normal.push(element);
        }
    });

    const renderProject = (project) => (
        <ProjectSummaries
            key={project.id}
            path={project.path}
            title={project.title}
            summary={project.summary}
            startDate={project.start_date}
            endDate={project.end_date}
        />
    );

    return (
        <div className="min-h-screen">
            <SideBar />
            <div className="px-6 pb-24 md:pl-24 md:pb-0 md:pr-12 lg:pl-32 lg:pr-20 py-12 md:py-16 lg:py-20">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-12 md:mb-16">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4">
                            <span className="gradient-text font-grotesk">Projects</span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-400 max-w-2xl">
                            Explore my work across software development, system administration, and embedded systems.
                        </p>
                    </div>

                    {/* Featured Projects */}
                    {featured.length > 0 && (
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <h2 className="text-2xl font-bold text-white">Featured Projects</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {featured.map(renderProject)}
                            </div>
                        </div>
                    )}

                    {/* All Projects */}
                    {normal.length > 0 && (
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-6">
                                {featured.length > 0 ? 'More Projects' : 'All Projects'}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {normal.map(renderProject)}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProjectsContainer = () => (
    <div className='flex flex-col min-h-fit'>
        <ProjectSummary />
    </div>
    
);

export default ProjectsContainer;
